<script setup lang="ts">
import { ref, computed, onMounted, watch } from 'vue'
import { router, usePage } from '@inertiajs/vue3'
import { trackEvent } from '@/composables/useAnalytics'
import AppLayout from '@/Layouts/AppLayout.vue'
import { Home, ChevronRight, AlertCircle, Send, History } from 'lucide-vue-next'

// UI Components
import {
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogFooter,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogAction,
} from '@/Components/ui/alert-dialog'

// Payment Components
import PaymentHeader from '@/Components/Payment/PaymentHeader.vue'
import TimeSlotSelection from '@/Components/Payment/TimeSlotSelection.vue'
import axios from 'axios'
import CardChooser from '@/Components/Payment/CardChooser.vue'
import PaymentElementForm from '@/Components/Payment/PaymentElementForm.vue'
import { NEW_CARD, type SavedCard } from '@/lib/savedCards'
import { loadStripe } from '@/lib/stripe'
import BillingAddressForm from '@/Components/Payment/BillingAddressForm.vue'
import OrderSummary from '@/Components/Payment/OrderSummary.vue'
import TrustSection from '@/Components/Payment/TrustSection.vue'
import HelpSection from '@/Components/Payment/HelpSection.vue'
import TermsCheckbox from '@/Components/Payment/TermsCheckbox.vue'
import AuthModal from '@/Components/Auth/AuthModal.vue'

// Declare Stripe on window
declare global {
  interface Window {
    Stripe: any
  }
}

interface Tutor {
  id: number
  slug: string
  name: string
  first_name: string
  last_name: string
  avatar?: string
  subjects: Array<{ id: number; name: string }>
  videos_count: number
}

interface Tier {
  id: number
  slug: string
  name: string
  description: string
  features: string[]
  price: number
}

interface SavedAddress {
  id: number
  first_name: string
  last_name: string
  email: string | null
  address1: string
  address2: string | null
  city: string
  postcode: string
  country: string
  is_billing_default: boolean
}

interface TimeSlot {
  day: string
  time: string
  label: string
}

interface Props {
  tutor: Tutor
  tier: Tier
  oneToOneAvailability?: TimeSlot[]
  validationWindowHours?: number
  firstLessonNoticeHours?: number
  stripeKey: string
  savedAddresses?: SavedAddress[]
  savedCards?: SavedCard[]
  userEmail?: string
}

const props = defineProps<Props>()

// Auth check - show blocking modal for guests
const page = usePage()
const isAuthenticated = computed(() => !!page.props.auth?.user)
const showAuthModal = ref(false)

onMounted(() => {
  if (!isAuthenticated.value) {
    showAuthModal.value = true
  }

  trackEvent('initiateCheckout', {
    tutor_name: props.tutor.name,
    tutor_id: props.tutor.id,
    tier: props.tier.name,
    value: props.tier.price,
    currency: 'GBP',
  })
})

function handleAuthSuccess() {
  // Reload page after successful auth to get updated user data
  window.location.reload()
}

// Form state
const timeSlot = ref<{ day: string; time: string; slotTag?: string; subjectId?: number; subjectName?: string; levelId?: number; levelName?: string }>({ day: '', time: '' })
const billingAddress = ref<{
  address_id?: number | null
  firstName: string
  lastName: string
  email: string
  streetAddress: string
  city: string
  postcode: string
  country: string
}>({
  address_id: null,
  firstName: '',
  lastName: '',
  email: '',
  streetAddress: '',
  city: '',
  postcode: '',
  country: 'United Kingdom',
})
const termsAccepted = ref(false)
const isProcessing = ref(false)

// Error display
const paymentError = ref('')
const showErrorDialog = computed(() => !!paymentError.value)

// Stripe refs
const stripeInstance = ref<any>(null)
const stripeElements = ref<any>(null)
const paymentFormComplete = ref(false)

/*
 * What the payment form is for: the first lesson's price, held rather than
 * taken, and the card kept for the lessons after it. The server creates the
 * payment with the same terms, or Stripe refuses it.
 */
const paymentOptions = computed(() => ({
  amount: Math.round(props.tier.price * 100),
  captureMethod: 'manual',
  currency: 'gbp',
  mode: 'payment',
  paymentMethodTypes: ['card'],
  setupFutureUsage: 'off_session',
}))

// The billing address form below already asks for these.
const paymentFields = { billingDetails: { address: 'never', email: 'never', name: 'never' } }

const showTimeSlotSelection = computed(() => props.tier.slug === 'gold')

/*
 * Which card this is going on. With several and no default they choose,
 * because which card a family meant to put a recurring commitment on is not
 * ours to guess. The card paid with here becomes the one lessons are charged to.
 */
const savedCards = computed<SavedCard[]>(() => props.savedCards ?? [])

const chosenCard = ref<string | null>(
  savedCards.value.length === 0
    ? NEW_CARD
    : savedCards.value.length === 1
      ? savedCards.value[0].id
      : savedCards.value.find(card => card.is_default)?.id ?? null
)

const usingANewCard = computed(() => chosenCard.value === NEW_CARD)

const payingWithASavedCard = computed(() => chosenCard.value !== null && !usingANewCard.value)

// A fresh form mounts each time, with nothing typed into it yet.
watch(usingANewCard, () => {
  paymentFormComplete.value = false
  stripeElements.value = null
})

// The review window, shown in hours to match how it's communicated elsewhere.
const reviewWindowText = computed(() => {
  const hours = props.validationWindowHours ?? 48
  return `${hours} ${hours === 1 ? 'hour' : 'hours'}`
})

// Validate form
const isFormValid = computed(() => {
  let hasTimeSlot = true
  if (showTimeSlotSelection.value) {
    // For gold tier: just need day and time
    // For silver tier: also need subject and level
    const hasBasicTimeSlot = timeSlot.value.day && timeSlot.value.time
    const hasCourseSelection = props.tier.slug === 'gold' || (timeSlot.value.subjectId && timeSlot.value.levelId)
    hasTimeSlot = hasBasicTimeSlot && hasCourseSelection
  }

  const hasBilling = billingAddress.value.firstName &&
    billingAddress.value.lastName &&
    billingAddress.value.email &&
    billingAddress.value.streetAddress &&
    billingAddress.value.city &&
    billingAddress.value.postcode

  // A card already on file needs nothing typed, so the Stripe form's readiness
  // is beside the point — it is not on screen.
  const hasACard = payingWithASavedCard.value || (stripeElements.value !== null && paymentFormComplete.value)

  return hasTimeSlot && hasBilling && termsAccepted.value && hasACard
})

function onStripeReady(stripe: any, elements: any) {
  stripeInstance.value = stripe
  stripeElements.value = elements
}

function onPaymentFormChange(complete: boolean) {
  paymentFormComplete.value = complete
}

async function handlePayment() {
  if (!isFormValid.value || isProcessing.value) return
  if (!payingWithASavedCard.value && !stripeElements.value) return

  isProcessing.value = true
  paymentError.value = ''

  try {
    const paymentSource = payingWithASavedCard.value
      ? chosenCard.value!
      : await tokenFromTheForm()

    if (!paymentSource) {
      isProcessing.value = false
      return
    }

    await placeTheHold(paymentSource)
  } catch (error: unknown) {
    paymentError.value = messageFrom(error)
    isProcessing.value = false
  }
}

/**
 * Turn what they entered into a Stripe confirmation token, which the server
 * uses to place the hold. Returns null when Stripe refuses it, having already
 * put the reason on screen.
 */
async function tokenFromTheForm(): Promise<string | null> {
  const { error: submitError } = await stripeElements.value.submit()

  if (submitError) {
    return null
  }

  const { confirmationToken, error } = await stripeInstance.value.createConfirmationToken({
    elements: stripeElements.value,
    params: {
      payment_method_data: {
        billing_details: {
          address: {
            city: billingAddress.value.city,
            country: 'GB',
            line1: billingAddress.value.streetAddress,
            line2: '',
            postal_code: billingAddress.value.postcode,
            state: '',
          },
          email: billingAddress.value.email,
          name: `${billingAddress.value.firstName} ${billingAddress.value.lastName}`,
        },
      },
    },
  })

  if (error) {
    paymentError.value = error.message
    return null
  }

  trackEvent('addPaymentInfo', {
    tutor_name: props.tutor.name,
    tier: props.tier.name,
    value: props.tier.price,
    currency: 'GBP',
  })

  return confirmationToken.id
}

/**
 * Ask the server to place the hold. Nothing is booked yet and the tutor is not
 * told anything: that happens only once the money is actually held.
 */
async function placeTheHold(paymentSource: string) {
  const { data } = await axios.post('/student/subscriptions/checkout', {
    billing_address: billingAddress.value,
    [paymentSource.startsWith('ctoken_') ? 'confirmation_token' : 'payment_method_id']: paymentSource,
    tier_id: props.tier.id,
    time_slot: showTimeSlotSelection.value ? timeSlot.value : null,
  })

  if (data.status === 'requires_action') {
    // Settled on this page, so a failure leaves the form on screen to try again.
    // No card form is mounted when paying with a saved card.
    const stripe = stripeInstance.value ?? await loadStripe(props.stripeKey)
    const { error: actionError } = await stripe.handleNextAction({ clientSecret: data.client_secret })

    if (actionError) {
      paymentError.value = actionError.message
        || 'Your bank did not approve the payment. Nothing has been charged — please try again.'
      isProcessing.value = false

      return
    }

    const finalised = await axios.post('/student/subscriptions/finalise', {
      payment_intent_id: data.payment_intent_id,
    })

    goToSuccess(finalised.data.subscription_id)

    return
  }

  if (data.status === 'complete') {
    goToSuccess(data.subscription_id)

    return
  }

  paymentError.value = 'We could not take payment on that card. Please try again or use a different card.'
  isProcessing.value = false
}

function goToSuccess(subscriptionId: number) {
  router.visit(`/student/subscriptions/success?subscription_id=${subscriptionId}`)
}

/** The server's own wording where there is one, never a raw exception. */
function messageFrom(error: unknown): string {
  const payload = (error as { response?: { data?: { message?: string; errors?: Record<string, string[]> } } })
    ?.response?.data

  if (payload?.message) {
    return payload.message
  }

  const firstFieldError = payload?.errors ? Object.values(payload.errors)[0]?.[0] : null

  return firstFieldError || 'Something went wrong taking payment. Nothing has been charged — please try again.'
}
</script>

<template>
  <AppLayout>
    <main class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <!-- Breadcrumb -->
      <section class="mb-6">
        <div class="flex items-center space-x-2 text-sm">
          <a href="/" class="text-gray-500 hover:text-gray-900 flex items-center gap-1">
            <Home class="w-4 h-4" />
            Home
          </a>
          <ChevronRight class="w-4 h-4 text-gray-400" />
          <a href="/tutors" class="text-gray-500 hover:text-gray-900">Tutors</a>
          <ChevronRight class="w-4 h-4 text-gray-400" />
          <a :href="`/tutors/${tutor.slug}`" class="text-gray-500 hover:text-gray-900">{{ tutor.name }}</a>
          <ChevronRight class="w-4 h-4 text-gray-400" />
          <span class="text-gray-900 font-medium">Payment</span>
        </div>
      </section>

      <div class="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <!-- Main Content -->
        <div class="lg:col-span-2 space-y-6">
          <PaymentHeader />

          <TimeSlotSelection
            v-if="showTimeSlotSelection"
            v-model="timeSlot"
            :one-to-one-availability="oneToOneAvailability"
            :first-lesson-notice-hours="firstLessonNoticeHours"
          />

          <CardChooser v-model="chosenCard" :cards="savedCards">
            <PaymentElementForm
              :fields="paymentFields"
              :options="paymentOptions"
              :stripe-key="stripeKey"
              @change="onPaymentFormChange"
              @ready="onStripeReady"
            />
          </CardChooser>

          <BillingAddressForm
            v-model="billingAddress"
            :saved-addresses="savedAddresses"
            :user-email="userEmail"
          />

          <TermsCheckbox v-model="termsAccepted" />

          <!-- Payment Button -->
          <section>
            <button
              @click="handlePayment"
              :disabled="!isFormValid || isProcessing"
              :class="[
                'w-full py-3 rounded-xl font-bold text-base transition-all shadow-lg flex items-center justify-center',
                isFormValid && !isProcessing
                  ? 'bg-gradient-to-r from-teal-500 to-teal-800 text-white hover:from-blue-700 hover:to-purple-700 hover:shadow-xl'
                  : 'bg-gray-300 text-gray-500 cursor-not-allowed',
              ]"
            >
              <Send v-if="!isProcessing" class="w-4 h-4 mr-2" />
              <svg v-else class="animate-spin w-4 h-4 mr-2" fill="none" viewBox="0 0 24 24">
                <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              {{ isProcessing ? 'Processing...' : `Submit Request — £${tier.price.toFixed(2)}` }}
            </button>
            <div class="mt-4 flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
              <History class="w-4 h-4 shrink-0 mt-0.5" />
              <span>
                <strong>Your tutor reviews your request first — you won't be charged until they accept.</strong>
                They have up to <strong>{{ reviewWindowText }}</strong> to respond. If they don't, your card is released and you're not charged.
              </span>
            </div>
            <p class="text-center text-sm text-gray-500 mt-3">
              Your payment information is secure and encrypted
            </p>
          </section>
        </div>

        <!-- Sidebar -->
        <div class="lg:col-span-1 space-y-6">
          <OrderSummary
            :tutor="tutor"
            :tier="tier"
            :videos-count="tutor.videos_count"
          />

          <TrustSection />

          <HelpSection />
        </div>
      </div>
    </main>

    <!-- Payment Error Dialog -->
    <AlertDialog :open="showErrorDialog" @update:open="(val: boolean) => { if (!val) paymentError = '' }">
      <AlertDialogContent class="rounded-2xl">
        <AlertDialogHeader>
          <div class="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
            <AlertCircle class="h-6 w-6 text-red-600" />
          </div>
          <AlertDialogTitle class="text-center">Payment Failed</AlertDialogTitle>
          <AlertDialogDescription class="text-center">
            {{ paymentError }}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter class="sm:justify-center">
          <AlertDialogAction
            class="bg-linear-to-r from-teal-500 to-teal-800 text-white hover:from-blue-700 hover:to-purple-700 rounded-xl px-8"
            @click="paymentError = ''"
          >
            Try Again
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>

    <!-- Auth Modal for Guests (not closeable) -->
    <AuthModal
      :show="showAuthModal"
      :closeable="false"
      initial-view="login"
      title="Sign in required"
      :subtitle="`Please sign in or create an account to subscribe to ${tutor.first_name}'s plan`"
      @success="handleAuthSuccess"
    />
  </AppLayout>
</template>
